import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Timestamp } from 'firebase/firestore';
import { estadiaClienteService } from '../../services/estadiaClienteService';
import { equipamentoService } from '../../services/equipamentoService';
import type { EstadiaClienteUpdate } from '../../models/estadiaCliente.types';
import type { AtivoGlpiEncontrado } from '../../services';
import type { ProcessoMovimentacao, MetodoEntrega } from '../../models';
import { dataInputParaDate } from '../../utils/dataInput';
import { SI_ESTADIAS_CLIENTE_QUERY_KEYS } from './useEstadiaClienteQuery';
import { SI_EQUIPAMENTOS_QUERY_KEYS } from './useEquipamentoQuery';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';

/** Só Monitor/Phone têm um tipo inequívoco — Computer é ambíguo (imac/macmini/macbook/
 * notebook), mesma limitação já tratada em VincularEquipamentoSection. Sem tipo derivável
 * (ou sem resultado do GLPI), cai em 'outros' — não tem intake de Movimentação aqui pra
 * servir de fallback melhor (Fase 1 é sempre antes de existir Solicitação). */
const TIPO_POR_GLPI_ITEMTYPE: Record<AtivoGlpiEncontrado['glpiItemType'], string | null> = {
  Monitor: 'monitor',
  Phone: 'smartphone',
  Computer: null,
};

interface EquipamentoParaEnviar {
  /** Ativo do Inventário já achado na tela (busca no Inventário) — caminho normal hoje. */
  equipamentoId?: string | null;
  patrimonio: string;
  nSerie?: string | null;
  modelo?: string | null;
  colaboradorCotadoId?: string | null;
  glpiItemId?: string | null;
  glpiItemType?: AtivoGlpiEncontrado['glpiItemType'];
  processador?: string | null;
  memoria?: string | null;
  tamanhoDisco?: string | null;
}

/** Acha (por patrimônio) ou cria o Equipamento — mesma lógica de useVincularEquipamentoMutation,
 * duplicada aqui de propósito (contextos diferentes o bastante — um dentro de Movimentação,
 * outro antes de existir Solicitação — pra não valer a pena uma abstração compartilhada
 * ainda). Nunca sobrescreve `statusCustodia`/dados de custódia aqui — quem decide isso é o
 * cascade de onEstadiaClienteWriteSyncEquipamentoCustody, a partir da estadia criada. */
async function encontrarOuCriarEquipamento(dados: EquipamentoParaEnviar): Promise<string> {
  if (dados.equipamentoId) return dados.equipamentoId;
  const existente = await equipamentoService.getByPatrimonio(dados.patrimonio);
  if (existente) {
    if (dados.processador || dados.memoria || dados.tamanhoDisco) {
      await equipamentoService.update({
        id: existente.id,
        processador: dados.processador ?? null,
        memoria: dados.memoria ?? null,
        tamanhoDisco: dados.tamanhoDisco ?? null,
      });
    }
    return existente.id;
  }

  return equipamentoService.create({
    patrimonio: dados.patrimonio,
    nSerie: dados.nSerie ?? null,
    modelo: dados.modelo ?? null,
    tipo: (dados.glpiItemType && TIPO_POR_GLPI_ITEMTYPE[dados.glpiItemType]) ?? 'outros',
    glpiItemId: dados.glpiItemId ?? null,
    glpiItemType: dados.glpiItemType ?? null,
    processador: dados.processador ?? null,
    memoria: dados.memoria ?? null,
    tamanhoDisco: dados.tamanhoDisco ?? null,
  });
}

/** Escrita de si_estadias_cliente — exclusivo de si_admin (ver firestore.rules). A
 * sincronização de `Equipamento.statusCustodia` acontece automaticamente via Cloud
 * Function (onEstadiaClienteWriteSyncEquipamentoCustody), nunca escrita direto daqui. */
export function useEstadiaClienteMutations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const ator = { id: user?.collaboratorId ?? user?.uid ?? '', name: user?.displayName ?? user?.email ?? '' };

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: SI_ESTADIAS_CLIENTE_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: SI_EQUIPAMENTOS_QUERY_KEYS.all });
  };

  /** Ação "Enviar máquina(s) para Cliente" — acha ou cria o Equipamento por patrimônio (o
   * inventário do GLPI só é lido, nem todo equipamento em estoque já está cadastrado em
   * si_equipamentos) e cria 1 EstadiaCliente por linha. Sempre representa um envio de
   * verdade (data/forma sempre conhecidas — decisão do usuário). Não é atômico (N
   * operações sequenciais) — aceitável dado o volume baixo esperado; retorna quais
   * falharam pra a UI avisar. */
  const criarEstadias = useMutation({
    mutationFn: async ({
      clienteId,
      equipamentos,
      motivo,
      dataEnvio,
      formaEnvio,
      codigoRastreio,
    }: {
      clienteId: string;
      equipamentos: EquipamentoParaEnviar[];
      motivo: ProcessoMovimentacao;
      /** Data digitada no formulário ("AAAA-MM-DD"). Uber/pessoalmente: essa é a data de
       * entrega (única); correios/outro: data de postagem (a de chegada é confirmada
       * depois, ver "Confirmar chegada"). */
      dataEnvio: string | null;
      formaEnvio: MetodoEntrega | null;
      codigoRastreio: string | null;
    }) => {
      const dataEnvioTimestamp = dataEnvio ? Timestamp.fromDate(dataInputParaDate(dataEnvio)) : Timestamp.now();
      // Mesmo padrão de MovimentacaoLogisticaSection: uber/pessoalmente entregam no mesmo
      // dia do envio — pula direto pra 'em_homologacao', sem passar por 'em_transito'.
      const usaDataUnica = formaEnvio === 'uber' || formaEnvio === 'pessoalmente';
      const resultados = await Promise.allSettled(
        equipamentos.map(async (dados) => {
          const equipamentoId = await encontrarOuCriarEquipamento(dados);
          return estadiaClienteService.create({
            equipamentoId,
            clienteId,
            status: usaDataUnica ? 'em_homologacao' : 'em_transito',
            motivo,
            colaboradorCotadoId: dados.colaboradorCotadoId ?? null,
            formaEnvio,
            codigoRastreio: formaEnvio === 'correios' ? codigoRastreio : null,
            dataEnvioRaro: dataEnvioTimestamp,
            dataChegadaCliente: usaDataUnica ? dataEnvioTimestamp : null,
            createdBy: ator,
          });
        })
      );
      const falhas = resultados
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r) => (r.reason instanceof Error ? r.reason.message : String(r.reason)));
      return { total: equipamentos.length, falhas };
    },
    onSuccess: ({ total, falhas }) => {
      invalidar();
      if (falhas.length > 0) {
        // Mostra o motivo real (ex. "Missing or insufficient permissions") em vez de só
        // uma contagem — sem isso não dava pra saber o que de fato falhou (ver correção).
        const motivos = Array.from(new Set(falhas)).join(' · ');
        toast.error(`${falhas.length} de ${total} equipamento(s) não foram registrados: ${motivos}`);
      } else {
        toast.success('Equipamento(s) registrados para o Cliente.');
      }
    },
    onError: (error: any) => {
      toast.error(`Erro ao enviar equipamentos para o Cliente: ${error?.message || 'erro desconhecido'}`);
    },
  });

  /** Cobre todas as transições de status (confirmar chegada, marcar pronta, retornar,
   * cancelar), trocar colaborador cotado, e adicionar chamado (a UI monta o array
   * `chamados` completo, incluindo o novo item, e reenvia — mesmo padrão já usado em
   * equipamentosIntake/equipamentosExecucao neste módulo). */
  const atualizarEstadia = useMutation({
    mutationFn: (data: EstadiaClienteUpdate) => estadiaClienteService.update({ ...data, updatedBy: ator }),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao atualizar estadia: ${error?.message || 'erro desconhecido'}`);
    },
  });

  /** Exclusão física de verdade — diferente de si_equipamentos, este registro é só o
   * rastreamento da passagem, não a entidade em si (ver firestore.rules). Se a estadia
   * ainda estava ativa, o cascade de onEstadiaClienteWriteSyncEquipamentoCustody reseta a
   * custódia do Equipamento de volta pro estoque. */
  const removerEstadia = useMutation({
    mutationFn: (id: string) => estadiaClienteService.delete(id),
    onSuccess: invalidar,
    onError: (error: any) => {
      toast.error(`Erro ao remover estadia: ${error?.message || 'erro desconhecido'}`);
    },
  });

  return { criarEstadias, atualizarEstadia, removerEstadia };
}
